/**
 * =============================================================================
 * SPEQ55 RFP Pro - Analysis API Functions
 * =============================================================================
 *
 * API functions for RFP analysis feature.
 *
 * @module api/analysisApi
 * @author SPEQ55
 */

import { get, post } from './client';

/**
 * Gets default keywords and technical specs for analysis.
 *
 * @param {string} rfpId - RFP UUID
 * @returns {Promise<{keywords: string[], technicalSpecs: string[]}>}
 */
export async function getAnalysisDefaults(rfpId) {
  const response = await get(`/rfps/${rfpId}/analysis/defaults`);
  return response.data.defaults;
}

/**
 * Custom error class for usage limit exceeded.
 */
export class UsageLimitError extends Error {
  constructor(message, usageData) {
    super(message);
    this.name = 'UsageLimitError';
    this.usageData = usageData;
  }
}

/**
 * Starts a new RFP analysis.
 *
 * @param {string} rfpId - RFP UUID
 * @param {Object} config - Analysis configuration
 * @param {string[]} config.keywords - Keywords to search for
 * @param {string[]} config.technicalSpecs - Technical specs to extract
 * @param {boolean} config.extendedMode - Use extended model
 * @returns {Promise<Object>} Analysis record
 * @throws {UsageLimitError} If monthly usage limit is exceeded
 */
export async function startAnalysis(rfpId, config) {
  try {
    const response = await post(`/rfps/${rfpId}/analysis`, config);
    return response.data.analysis;
  } catch (error) {
    // Handle 429 Too Many Requests (usage limit exceeded)
    if (error.response && error.response.status === 429) {
      const errorData = error.response.data;
      throw new UsageLimitError(
        errorData.error || 'Monthly analysis limit reached',
        errorData.usage || {}
      );
    }
    throw error;
  }
}

/**
 * Lists all analyses for an RFP.
 *
 * @param {string} rfpId - RFP UUID
 * @returns {Promise<Object[]>} Analysis records
 */
export async function listAnalyses(rfpId) {
  const response = await get(`/rfps/${rfpId}/analysis`);
  return response.data.analyses;
}

/**
 * Gets a specific analysis.
 *
 * @param {string} rfpId - RFP UUID
 * @param {string} analysisId - Analysis UUID
 * @returns {Promise<Object>} Analysis record
 */
export async function getAnalysis(rfpId, analysisId) {
  const response = await get(`/rfps/${rfpId}/analysis/${analysisId}`);
  return response.data.analysis;
}

/**
 * Gets chat history for an analysis.
 *
 * @param {string} rfpId - RFP UUID
 * @param {string} analysisId - Analysis UUID
 * @returns {Promise<Object[]>} Chat messages
 */
export async function getChatHistory(rfpId, analysisId) {
  const response = await get(`/rfps/${rfpId}/analysis/${analysisId}/chat`);
  return response.data.messages;
}

/**
 * Sends a chat message and returns an EventSource for streaming.
 *
 * @param {string} rfpId - RFP UUID
 * @param {string} analysisId - Analysis UUID
 * @param {string} question - User's question
 * @param {Object} callbacks - Event callbacks
 * @param {Function} callbacks.onMessage - Called with each text chunk
 * @param {Function} callbacks.onDone - Called when streaming completes
 * @param {Function} callbacks.onError - Called on error
 * @returns {Promise<void>}
 */
export async function sendChatMessage(rfpId, analysisId, question, callbacks) {
  const { onMessage, onDone, onError } = callbacks;

  try {
    // Get auth token
    const token = localStorage.getItem('auth_token');
    const baseUrl = import.meta.env.VITE_API_URL || '/api';

    // Use fetch with streaming
    const response = await fetch(`${baseUrl}/rfps/${rfpId}/analysis/${analysisId}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ question }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send message');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        onDone?.();
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.text) {
              onMessage?.(data.text);
            }
            if (data.error) {
              onError?.(new Error(data.error));
            }
          } catch {
            // Ignore parse errors for non-JSON data lines
          }
        } else if (line.startsWith('event: done')) {
          // Stream complete - onDone will be called when reader is done
        } else if (line.startsWith('event: error')) {
          // Error event - data follows on next line
        }
      }
    }
  } catch (error) {
    onError?.(error);
    throw error;
  }
}

/**
 * Downloads the PDF report for an analysis.
 *
 * @param {string} rfpId - RFP UUID
 * @param {string} analysisId - Analysis UUID
 * @param {string} rfpNumber - RFP number for filename
 * @returns {Promise<void>}
 */
export async function downloadAnalysisPDF(rfpId, analysisId, rfpNumber) {
  const token = localStorage.getItem('auth_token');
  const baseUrl = import.meta.env.VITE_API_URL || '/api';

  const response = await fetch(`${baseUrl}/rfps/${rfpId}/analysis/${analysisId}/pdf`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to download PDF');
  }

  // Get the blob and create download link
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `RFP-Analysis-${rfpNumber || rfpId}-${new Date().toISOString().split('T')[0]}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Downloads the CSV export for an analysis.
 *
 * @param {string} rfpId - RFP UUID
 * @param {string} analysisId - Analysis UUID
 * @param {string} rfpNumber - RFP number for filename
 * @returns {Promise<void>}
 */
export async function downloadAnalysisCSV(rfpId, analysisId, rfpNumber) {
  const token = localStorage.getItem('auth_token');
  const baseUrl = import.meta.env.VITE_API_URL || '/api';

  const response = await fetch(`${baseUrl}/rfps/${rfpId}/analysis/${analysisId}/csv`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to download CSV');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `RFP-Analysis-${rfpNumber || rfpId}-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Downloads the structured output schema as a JSON file.
 *
 * @returns {Promise<void>}
 */
export async function downloadOutputSchema() {
  const token = localStorage.getItem('auth_token');
  const baseUrl = import.meta.env.VITE_API_URL || '/api';

  const response = await fetch(`${baseUrl}/analysis/output-schema/download`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to download schema');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  // Extract filename from Content-Disposition header or use default
  const disposition = response.headers.get('Content-Disposition');
  const filenameMatch = disposition && disposition.match(/filename="(.+)"/);
  link.download = filenameMatch ? filenameMatch[1] : 'speq55-rfp-analysis-schema.json';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default {
  getAnalysisDefaults,
  startAnalysis,
  listAnalyses,
  getAnalysis,
  getChatHistory,
  sendChatMessage,
  downloadAnalysisPDF,
  downloadAnalysisCSV,
  downloadOutputSchema,
};
